import { useState } from "react";
import { ChevronRight, MoreVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { isNoChangesNeeded, type CosmoQuestion } from "@/types/scorecard";
import { capitalizeFirst } from "@/utils";
import { getStatusConfig } from "./constants";
import { DeltaBadge } from "./DeltaBadge";

interface QuestionRowProps {
  question: CosmoQuestion;
  forceExpand?: boolean;
  onCreateAbTest?: (question: CosmoQuestion) => void;
}

export function QuestionRow({ question: q, forceExpand, onCreateAbTest }: QuestionRowProps) {
  const [open, setOpen] = useState(false);
  const cfg = getStatusConfig(q.status);
  const Icon = cfg.icon;
  const isOpen = forceExpand || open;
  // FR-6/FR-7: the menu is absent, never merely inactive — a running test or a "no
  // changes needed" verdict means there is nothing to test, so it does not render at all.
  const canCreateAbTest = !q.hasRunningTest && !isNoChangesNeeded(q);
  // AUT-441: a question the model excluded from scoring carries a `score` of 0 and, usually,
  // a placeholder Evidence ("No references found.") and Recommendation ("None."). Drawing
  // those reads as a real zero-scoring gap. The design shows the word `N/A` where the number
  // goes and keeps only the Finding, which is the one field that says WHY it was excluded.
  const isNotApplicable = q.status === "not_applicable";

  return (
    <div className="bg-card">
      <div className="flex items-center hover:bg-muted/20 transition-colors group">
        <button
          onClick={() => setOpen(!open)}
          className="flex-1 min-w-0 flex items-center gap-3 px-4 py-3 text-left"
        >
          <ChevronRight className={`h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform duration-200 ${isOpen ? "rotate-90" : ""}`} />
          <div className="flex-1 min-w-0">
            <span className="text-sm font-normal text-foreground">{q.question}</span>
          </div>
          <Badge variant="outline" className={`text-[10px] font-semibold shrink-0 gap-1 ${cfg.badgeClass}`}>
            <Icon className={`h-3 w-3 ${cfg.color}`} />
            {cfg.label}
          </Badge>
          <span className={`text-sm font-semibold w-12 text-right ${isNotApplicable ? "text-muted-foreground" : "text-foreground"}`}>
            {isNotApplicable ? (
              "N/A"
            ) : (
              <>
                {q.score}
                <DeltaBadge current={q.score} previous={q.previousScore} />
              </>
            )}
          </span>
        </button>

        {onCreateAbTest && canCreateAbTest && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="w-8 mr-3 shrink-0" aria-label="Question actions">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-popover">
              <DropdownMenuItem onClick={() => onCreateAbTest(q)}>
                Create A/B test
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>

      {isOpen && (
        <div className="mx-4 mb-3 ml-11 p-4 space-y-3">
          <div>
            <p className="text-xs font-semibold text-foreground mb-1">Finding</p>
            <p className="text-sm text-muted-foreground">{capitalizeFirst(q.finding)}</p>
          </div>
          {!isNotApplicable && (
            <>
              {q.evidenceSnippet && (
                <div>
                  <p className="text-xs font-semibold text-foreground mb-1">Evidence</p>
                  <p className="text-sm text-muted-foreground">{capitalizeFirst(q.evidenceSnippet)}</p>
                </div>
              )}
              <div>
                <p className="text-xs font-semibold text-foreground mb-1">Recommendation</p>
                <p className="text-sm text-muted-foreground">{capitalizeFirst(q.recommendation)}</p>
              </div>
              {q.changeNote && (
                <div>
                  <p className="text-xs font-semibold text-foreground mb-1">What Changed</p>
                  <p className="text-sm text-muted-foreground">{capitalizeFirst(q.changeNote)}</p>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
